import { Formik } from "formik";
import { Button, CardBody, Col, Form, FormGroup, Input, Row } from "reactstrap";
import * as Yup from 'yup';
import SelectField from "../Utils/SelectField";
import { LabelText } from "../Utils/Input"; // Assuming this component exists
import { useDispatch } from "react-redux";
import { useState } from "react";
import { addMidSession } from "../../slices/MidSession/AddMidsession/thunk";
import { editMidSession } from "../../slices/MidSession/UpdateMidSession/thunk";
import CommonButton from "../Utils/CommonButton";

export const MidSessionForm = ({ extractedData, toggleModals, setModalOpen, initialData, isEditMode }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const usageEventOptions = [...new Set(extractedData.map(({ eventType }) => eventType))].map(eventType => ({
    label: eventType,
    value: eventType
  }));

  let initialValues = {
    triggerConfigurationId: isEditMode ? initialData.triggerConfigId : "",
    usageEventType: isEditMode && initialData.usageEventType ? {
      label: initialData.usageEventType,
      value: initialData.usageEventType
    } : null,
    timeInterval: isEditMode ? initialData.triggerConditions?.timeInterval?.intervalUnitInSeconds : "",
    timeIntervalPriority: isEditMode ? initialData.triggerConditions?.timeInterval?.priority : "",
    volumeThreshold: isEditMode ? initialData.triggerConditions?.volumeThreshold?.thresholdUnit : "",
    volumeThresholdPriority: isEditMode ? initialData.triggerConditions?.volumeThreshold?.priority : "",
    balanceThreshold: isEditMode ? initialData.triggerConditions?.balanceThreshold?.thresholdAmount : "",
    balanceThresholdPriority: isEditMode ? initialData.triggerConditions?.balanceThreshold?.priority : "",
  };

  const validationSchema = Yup.object().shape({
    triggerConfigurationId: Yup.string().required("Enter Trigger Configuration ID"),
    usageEventType: Yup.object().required("Select Usage Event Type"),
    
    // Individual field validation with custom test
    timeInterval: Yup.string().test(
      'at-least-one-threshold',
      'At least one threshold field must be provided',
      function(value) {
        const { volumeThreshold, balanceThreshold } = this.parent;
        return value || volumeThreshold || balanceThreshold;
      }
    ),
    
    volumeThreshold: Yup.string().test(
      'at-least-one-threshold',
      'At least one threshold field must be provided',
      function(value) {
        const { timeInterval, balanceThreshold } = this.parent;
        return value || timeInterval || balanceThreshold;
      }
    ),
    
    balanceThreshold: Yup.string().test(
      'at-least-one-threshold',
      'At least one threshold field must be provided',
      function(value) {
        const { timeInterval, volumeThreshold } = this.parent;
        return value || timeInterval || volumeThreshold;
      }
    ),
    
    // Priority fields remain optional
    timeIntervalPriority: Yup.string(),
    volumeThresholdPriority: Yup.string(),
    balanceThresholdPriority: Yup.string(),
  });

  return (
    <div>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={async (values) => {
          const payload = {
            triggerConfigId: values.triggerConfigurationId || "",
            usageEventType: values.usageEventType?.value || "",
            triggerConditions: {
              timeInterval: {
                intervalUnitInSeconds: parseInt(values.timeInterval, 10) || 0,
                priority: parseInt(values.timeIntervalPriority, 10) || 0
              },
              volumeThreshold: {
                thresholdUnit: parseInt(values.volumeThreshold, 10) || 0,
                priority: parseInt(values.volumeThresholdPriority, 10) || 0
              },
              balanceThreshold: {
                thresholdAmount: parseInt(values.balanceThreshold, 10) || 0,
                priority: parseInt(values.balanceThresholdPriority, 10) || 0
              }
            }
          };

          if (!isEditMode) {
            dispatch(addMidSession(payload, setLoading, setModalOpen));
          } else {
            dispatch(editMidSession(payload, initialData.usageEventType, setLoading, setModalOpen));
          }
        }}
      >
        {({
          handleChange,
          handleSubmit,
          errors,
          touched,
          values,
          setFieldValue,
        }) => (
          <Form className="needs-validation" onSubmit={handleSubmit}>
            <Col lg={12}>
              <Row className="gy-1 px-3">
                <CardBody>
                  <Row>
                    <Col xxl={6} md={6} className="pe-0 mb-1">
                      <FormGroup>
                        <LabelText
                          htmlFor={"triggerConfigurationId"}
                          classLabelName={"ms-0 rv-form-label"}
                          className={"text-danger"}
                          displayText={"Trigger Configuration ID"}
                          important={"*"}
                        />
                        <LabelText
                          htmlFor={"triggerConfigurationId"}
                          classLabelName={`d-block ms-0 rv-sub-label ${
                            touched.triggerConfigurationId && errors.triggerConfigurationId
                              ? "rv-label-danger"
                              : "rv-label-muted"
                          }`}
                          displayText={
                            touched.triggerConfigurationId && errors.triggerConfigurationId
                              ? errors.triggerConfigurationId
                              : "Enter Trigger Configuration ID"
                          }
                        />
                        <Input
                          placeholder=""
                          type="text"
                          name="triggerConfigurationId"
                          onChange={handleChange}
                          value={values.triggerConfigurationId || ''}
                          className={`${
                            touched.triggerConfigurationId && errors.triggerConfigurationId
                              ? "error-input"
                              : ""
                          }`}
                          disabled={isEditMode}
                        />
                      </FormGroup>
                    </Col>

                    <Col xxl={6} md={6} className="pe-0 mb-1">
                      <FormGroup>
                        <LabelText
                          htmlFor={"usageEventType"}
                          classLabelName={"ms-0 rv-form-label"}
                          className={"text-danger"}
                          displayText={"Usage Event Type"}
                          important={"*"}
                        />
                        <LabelText
                          htmlFor={"usageEventType"}
                          classLabelName={`d-block ms-0 rv-sub-label ${
                            touched.usageEventType && errors.usageEventType
                              ? "rv-label-danger"
                              : "rv-label-muted"
                          }`}
                          displayText={
                            touched.usageEventType && errors.usageEventType
                              ? errors.usageEventType
                              : "Select an event usage"
                          }
                        />
                        <SelectField
                          placeholder=""
                          options={usageEventOptions}
                          value={values.usageEventType || null}
                          className={`${
                            touched.usageEventType && !values.usageEventType ? "error-input" : ""
                          }`}
                          handleChange={(e) => setFieldValue("usageEventType", e)}
                          isDisabled={isEditMode}
                          isRVStyle={true}
                        />
                      </FormGroup>
                    </Col>

                    <Col xxl={6} md={6} className="pe-0 mb-1">
                      <FormGroup>
                        <LabelText
                          htmlFor={"timeInterval"}
                          classLabelName={"ms-0 rv-form-label"}
                          className={"text-danger"}
                          displayText={"Time Interval"}
                          important={""}
                        />
                        <LabelText
                          htmlFor={"timeInterval"}
                          classLabelName={`d-block ms-0 rv-sub-label ${
                            touched.timeInterval && errors.timeInterval
                              ? "rv-label-danger"
                              : "rv-label-muted"
                          }`}
                          displayText={
                            touched.timeInterval && errors.timeInterval
                              ? errors.timeInterval
                              : "Enter Interval Unit in Seconds"
                          }
                        />
                        <Input
                          placeholder=""
                          type="text"
                          name="timeInterval"
                          onChange={handleChange}
                          value={values.timeInterval || ''}
                          className={`${
                            touched.timeInterval && errors.timeInterval ? "error-input" : ""
                          }`}
                        />
                      </FormGroup>
                    </Col>

                

                    <Col xxl={6} md={6} className="pe-0 mb-1">
                      <FormGroup>
                        <LabelText
                          htmlFor={"volumeThreshold"}
                          classLabelName={"ms-0 rv-form-label"}
                          className={"text-danger"}
                          displayText={"Volume Threshold"}
                          important={""}
                        />
                        <LabelText
                          htmlFor={"volumeThreshold"}
                          classLabelName={`d-block ms-0 rv-sub-label ${
                            touched.volumeThreshold && errors.volumeThreshold
                              ? "rv-label-danger"
                              : "rv-label-muted"
                          }`}
                          displayText={
                            touched.volumeThreshold && errors.volumeThreshold
                              ? errors.volumeThreshold
                              : "Enter Threshold Unit"
                          }
                        />
                        <Input
                          placeholder=""
                          type="text"
                          name="volumeThreshold"
                          onChange={handleChange}
                          value={values.volumeThreshold || ''}
                          className={`${
                            touched.volumeThreshold && errors.volumeThreshold ? "error-input" : ""
                          }`}
                        />
                      </FormGroup>
                    </Col>

                

                    <Col xxl={6} md={6} className="pe-0 mb-1">
                      <FormGroup>
                        <LabelText
                          htmlFor={"balanceThreshold"}
                          classLabelName={"ms-0 rv-form-label"}
                          className={"text-danger"}
                          displayText={"Balance Threshold"}
                          important={""}
                        />
                        <LabelText
                          htmlFor={"balanceThreshold"}
                          classLabelName={`d-block ms-0 rv-sub-label ${
                            touched.balanceThreshold && errors.balanceThreshold
                              ? "rv-label-danger"
                              : "rv-label-muted"
                          }`}
                          displayText={
                            touched.balanceThreshold && errors.balanceThreshold
                              ? errors.balanceThreshold
                              : "Enter Threshold Amount"
                          }
                        />
                        <Input
                          placeholder=""
                          type="text"
                          name="balanceThreshold"
                          onChange={handleChange}
                          value={values.balanceThreshold || ''}
                          className={`${
                            touched.balanceThreshold && errors.balanceThreshold ? "error-input" : ""
                          }`}
                        />
                      </FormGroup>
                    </Col>

                  </Row>
                </CardBody>
              </Row>
            </Col>

            <Row className="justify-content-end mb-2 mt-2">
              <div className="live-preview">
                <div className="d-flex justify-content-center gap-2 mt-2">
                  <CommonButton
                    outline={true}
                    className={
                      "btn-label rv-secondary-btn rounded-1 px-1 ps-3 py-1 w-md-auto text-nowrap"
                    }
                    color={"main-color"}
                    type={"button"}
                    buttonAction={toggleModals}
                    buttonText={"Close"}
                    icon={
                      <i
                        className="ri-close-fill label-icon align-middle fs-16 me-1"
                        style={{ transform: "scaleX(-1)", borderLeft: "1px solid" }}
                      >
                      </i>
                    }
                    btnTxtClsName={"px-2 ps-3 ms-2 me-1"}
                  />
                  <CommonButton
                    color={"secondary"}
                    type="submit"
                    buttonText={"Submit"}
                    icon={<i className="ri-checkbox-circle-line label-icon align-middle fs-16 me-1"></i>}
                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap"
                    btnTxtClsName={"px-2 ps-3 ms-2 me-1"}
                  />
                </div>
              </div>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
};